import { cn } from "@/lib/utils";
import { ChevronDown, Search } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchSuggestions } from "../api";
import type { Suggestion } from "../types";
import { getSummonerIconUrl } from "../utils/ddragon";
import { getRecentSearches, getSavedRegion, parseRiotId, REGIONS, rememberSearch, saveRegion, summonerPath } from "../utils/regions";

interface SearchBarProps {
	/** lg = the home page hero, sm = the header */
	size?: "lg" | "sm";
	autoFocus?: boolean;
}

export const REGION_NAMES: Record<string, string> = {
	euw: "Europe West", eune: "Europe Nordic & East", na: "North America", kr: "Korea", br: "Brazil", jp: "Japan",
	lan: "Latin America North", las: "Latin America South", oce: "Oceania", tr: "Türkiye", ru: "Russia",
	me: "Middle East", sg: "Singapore", tw: "Taiwan", vn: "Vietnam"
};

interface Option {
	name: string;
	tag: string;
	region: string;
	icon?: number;
	level?: number | null;
}

/**
 * Region + "Name#TAG" search, as an ARIA combobox.
 * Empty + focused: recent searches. 2+ chars: autocomplete over summoners we have already seen.
 * Keys: ↑/↓ move, Enter opens the highlighted option (or submits), Escape closes (a second Escape clears).
 */
export function SearchBar({ size = "sm", autoFocus = false }: SearchBarProps) {
	const navigate = useNavigate();
	const listId = useId();
	const [region, setRegion] = useState(getSavedRegion);
	const [regionTouched, setRegionTouched] = useState(false);
	const [value, setValue] = useState("");
	const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
	const [open, setOpen] = useState(false);
	const [active, setActive] = useState(-1);
	const [error, setError] = useState<string | null>(null);
	const box = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (value.trim().length < 2) {
			setSuggestions([]);
			return;
		}
		let cancelled = false;
		const timer = setTimeout(async () => {
			const found = await fetchSuggestions(value, region);
			if (!cancelled) setSuggestions(found);
		}, 200);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [value, region]);

	useEffect(() => {
		const close = (e: MouseEvent) => {
			if (!box.current?.contains(e.target as Node)) setOpen(false);
		};
		document.addEventListener("mousedown", close);
		return () => document.removeEventListener("mousedown", close);
	}, []);

	const typed = value.trim().length >= 2;
	const options: Option[] = typed
		? suggestions.map(s => ({ name: s.name, tag: s.tagLine, region: s.region, icon: s.profileIconId, level: s.summonerLevel }))
		: value.trim() === ""
			? getRecentSearches().map(r => ({ name: r.name, tag: r.tag, region: r.region, icon: r.profileIconId }))
			: [];
	const heading = typed ? `Summoners in ${REGIONS.find(r => r.slug === region)?.label ?? region.toUpperCase()}` : options.length ? "Recent" : "";
	const showList = open && (options.length > 0 || typed);
	const activeIndex = Math.min(active, options.length - 1);

	const go = (name: string, tag: string, slug: string) => {
		rememberSearch({ name, tag, region: slug });
		saveRegion(slug);
		setRegion(slug);
		setValue("");
		setOpen(false);
		setActive(-1);
		setError(null);
		navigate(summonerPath(slug, name, tag));
	};

	const submit = (e: React.FormEvent) => {
		e.preventDefault();
		const id = parseRiotId(value);
		if (!id) {
			setError("Enter a Riot ID like Name#TAG. The tag is after the # in your client profile.");
			setOpen(false);
			return;
		}
		go(id.name, id.tag, region);
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		switch (e.key) {
			case "ArrowDown":
				e.preventDefault();
				setOpen(true);
				setActive(i => Math.min(i + 1, options.length - 1));
				break;
			case "ArrowUp":
				e.preventDefault();
				setActive(i => Math.max(i - 1, -1));
				break;
			case "Enter":
				if (showList && activeIndex >= 0) {
					e.preventDefault();
					const o = options[activeIndex]!;
					go(o.name, o.tag, o.region);
				}
				break;
			case "Escape":
				if (showList) {
					e.preventDefault();
					setOpen(false);
					setActive(-1);
				} else setValue("");
				break;
		}
	};

	const big = size === "lg";
	const optionId = (i: number) => `${listId}-opt-${i}`;

	return (
		<div ref={box} className="relative w-full">
			<form
				role="search"
				onSubmit={submit}
				className={cn(
					"flex items-stretch border bg-surface transition-colors focus-within:border-signal",
					error ? "border-ink" : "border-line-2",
					big ? "h-[58px] rounded-xl" : "h-10 rounded-lg"
				)}
			>
				<label className="relative flex items-center border-r border-line">
					<span className="sr-only">Region</span>
					<select
						value={region}
						onChange={e => {
							setRegion(e.target.value);
							saveRegion(e.target.value);
							setRegionTouched(true);
							setActive(-1);
						}}
						className={cn(
							"h-full cursor-pointer appearance-none border-0 bg-transparent font-bold tracking-[.06em] text-ink focus-visible:outline-offset-[-3px]",
							big ? "pl-[18px] pr-[34px] text-sm" : "pl-3 pr-[26px] text-xs"
						)}
					>
						{REGIONS.map(r => (
							<option key={r.slug} value={r.slug} className="bg-surface">
								{r.label}
							</option>
						))}
					</select>
					<ChevronDown aria-hidden="true" className={cn("pointer-events-none absolute text-ink-3", big ? "right-3 h-3.5 w-3.5" : "right-2 h-3 w-3")} />
				</label>
				<input
					type="text"
					role="combobox"
					aria-label="Riot ID"
					aria-autocomplete="list"
					aria-expanded={showList}
					aria-controls={listId}
					aria-activedescendant={showList && activeIndex >= 0 ? optionId(activeIndex) : undefined}
					aria-invalid={!!error}
					aria-describedby={error ? `${listId}-err` : undefined}
					autoFocus={autoFocus}
					value={value}
					placeholder={big ? "Game name + #TAG" : "Name#TAG"}
					autoComplete="off"
					autoCapitalize="off"
					spellCheck={false}
					enterKeyHint="search"
					onChange={e => {
						setValue(e.target.value);
						setError(null);
						setOpen(true);
						setActive(-1);
					}}
					onFocus={() => setOpen(true)}
					onKeyDown={onKeyDown}
					className={cn(
						"min-w-0 flex-1 border-0 bg-transparent text-ink placeholder:text-ink-3 focus:outline-none",
						big ? "px-4 text-[17px]" : "px-3 text-sm"
					)}
				/>
				<button
					type="submit"
					aria-label="Search"
					className={cn(
						"flex cursor-pointer items-center justify-center gap-2 bg-signal font-bold text-on-signal transition-colors hover:bg-signal-hover",
						big ? "rounded-r-[11px] px-5 text-[15px]" : "w-11 rounded-r-[7px]"
					)}
				>
					<Search aria-hidden="true" className={big ? "h-[18px] w-[18px]" : "h-[17px] w-[17px]"} strokeWidth={2.5} />
					{big && <span className="hidden sm:inline">Search</span>}
				</button>
			</form>

			{big && (
				<div className="mt-2.5 flex flex-wrap justify-between gap-x-4 gap-y-1.5 text-[13px] text-ink-3">
					{error ? (
						<span id={`${listId}-err`} role="alert" className="text-ink">
							{error}
						</span>
					) : (
						<span>
							Searching <span className="font-semibold text-ink">{REGION_NAMES[region] ?? region.toUpperCase()}</span>
						</span>
					)}
					{regionTouched && (
						<span aria-live="polite" className="text-ink-2">
							Region saved for next time
						</span>
					)}
				</div>
			)}
			{!big && error && (
				<div id={`${listId}-err`} role="alert" className="absolute left-0 top-[46px] z-50 rounded-md border border-line-2 bg-raised px-2.5 py-1.5 text-xs text-ink">
					Enter a Riot ID like Name#TAG
				</div>
			)}

			{showList && (
				<div
					className={cn(
						"absolute inset-x-0 z-50 overflow-hidden border border-line-2 bg-surface shadow-[0_18px_40px_rgba(0,0,0,.5)] animate-reveal",
						big ? "top-16 rounded-xl" : "top-[46px] rounded-[10px]"
					)}
				>
					{heading && <div className="px-3.5 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-[.09em] text-ink-3">{heading}</div>}
					<ul id={listId} role="listbox" aria-label="Summoner suggestions" className="m-0 list-none p-1.5">
						{options.map((o, i) => (
							<li
								key={`${o.region}:${o.name}#${o.tag}`}
								id={optionId(i)}
								role="option"
								aria-selected={i === activeIndex}
								onMouseDown={e => {
									e.preventDefault();
									go(o.name, o.tag, o.region);
								}}
								onMouseEnter={() => setActive(i)}
								className={cn("flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2", i === activeIndex && "bg-[#232a37]")}
							>
								<img src={getSummonerIconUrl(o.icon ?? 29)} alt="" className={cn("shrink-0 rounded-[7px]", big ? "h-[34px] w-[34px]" : "h-[30px] w-[30px]")} />
								<span className={cn("min-w-0 flex-1 truncate font-semibold", big ? "text-[15px]" : "text-sm")}>
									{o.name}
									<span className="font-medium text-ink-3">#{o.tag}</span>
								</span>
								{o.level != null && <span className="text-xs text-ink-3">Lv {o.level}</span>}
								<span className="rounded bg-raised px-1.5 py-0.5 text-[11px] font-bold tracking-[.06em] text-ink-2">{o.region.toUpperCase()}</span>
							</li>
						))}
					</ul>
					{typed && options.length === 0 && (
						<div className="px-4 pt-1 pb-3.5 text-sm text-ink-2">
							No saved summoner matches. Press Enter to look up <span className="font-semibold text-ink">{value.trim()}</span>.
						</div>
					)}
					<div className="hidden gap-4 border-t border-line px-3.5 py-2 font-mono text-[11px] text-ink-3 sm:flex" aria-hidden="true">
						<span>↑↓ move</span>
						<span>↵ open</span>
						<span>esc close</span>
					</div>
				</div>
			)}
		</div>
	);
}
